/**
 * API Error Classes
 * Shared error types for ReKindle API clients
 *
 * @package @rekindle/shared
 */

export interface ApiErrorResponse {
  error: string;
  message?: string;
  details?: unknown;
  code?: string;
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(
    message: string,
    statusCode: number,
    code: string = 'API_ERROR',
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  static fromResponse(response: ApiErrorResponse, statusCode: number): ApiError {
    // Backend wraps errors as { error: { code, message, details } }
    // but ApiErrorResponse types `error` as string — handle both shapes.
    const errorPayload = response.error as unknown;
    const nested =
      typeof errorPayload === 'object' && errorPayload !== null
        ? (errorPayload as { message?: string; code?: string; details?: unknown })
        : null;

    const message =
      nested?.message ||
      response.message ||
      (typeof errorPayload === 'string' ? errorPayload : undefined) ||
      'Unknown error';

    const code = nested?.code || response.code || 'API_ERROR';
    const details = nested?.details || response.details;

    return new ApiError(message, statusCode, code, details);
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Session expired - please log in again', details?: unknown) {
    super(message, 401, 'UNAUTHORIZED', details);
    this.name = 'UnauthorizedError';
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string = 'You do not have permission to access this resource', details?: unknown) {
    super(message, 403, 'FORBIDDEN', details);
    this.name = 'ForbiddenError';
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string = 'Resource not found', details?: unknown) {
    super(message, 404, 'NOT_FOUND', details);
    this.name = 'NotFoundError';
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

export class ValidationError extends ApiError {
  constructor(message: string = 'Validation failed', details?: unknown) {
    super(message, 400, 'VALIDATION_ERROR', details);
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class NetworkError extends ApiError {
  constructor(message: string = 'Network error - check your internet connection') {
    super(message, 0, 'NETWORK_ERROR');
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

export class TimeoutError extends ApiError {
  constructor(message: string = 'Request timeout - server took too long to respond') {
    super(message, 408, 'TIMEOUT');
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

export class OfflineError extends ApiError {
  constructor(message: string = 'You are offline') {
    super(message, 0, 'OFFLINE');
    this.name = 'OfflineError';
    Object.setPrototypeOf(this, OfflineError.prototype);
  }
}

export class UsageLimitError extends ApiError {
  readonly currentUsage?: number;
  readonly limit?: number;
  readonly planTier?: string;
  readonly feature?: string;
  readonly reason?: string;

  constructor(
    message: string = 'Usage limit exceeded',
    details?: {
      reason?: string;
      currentUsage?: number;
      limit?: number;
      planTier?: string;
      feature?: string;
    }
  ) {
    super(message, 429, 'USAGE_LIMIT_EXCEEDED', details);
    this.name = 'UsageLimitError';
    this.currentUsage = details?.currentUsage;
    this.limit = details?.limit;
    this.planTier = details?.planTier;
    this.feature = details?.feature;
    this.reason = details?.reason;
    Object.setPrototypeOf(this, UsageLimitError.prototype);
  }
}
